import { Controller } from "@hotwired/stimulus"
import { loadGoogleMaps } from "helpers/google_maps_loader"

export default class GoogleMapController extends Controller {
  static values = {
    markers: Array,
    mapId: String,
    apiKey: String,
  }

  async connect() {
    this.markersById = {}
    await loadGoogleMaps(this.apiKeyValue)
    await this.#renderMap()
  }

  async reconnect() {
    await this.#renderMap()
  }

  disconnect() {
    this.#cleanup()
  }

  highlightMarker({ detail: { id } }) {
    this.clearHighlight()

    const marker = this.markersById[id]
    if (!marker) return

    this.highlightedMarker = marker
    marker.infoWindow.open({ anchor: marker.markerElement, map: this.map })
    this.#scaleMarker(marker.markerElement, "scale(1.2)")

    if (!this.map.getBounds().contains(marker.markerElement.position)) {
      this.map.panTo(marker.markerElement.position)
    }
  }

  clearHighlight() {
    if (!this.highlightedMarker) return

    this.highlightedMarker.infoWindow.close()
    this.#scaleMarker(this.highlightedMarker.markerElement, "")
    this.highlightedMarker = null
  }

  async #renderMap() {
    await this.#createMapInstance()
    await this.#addMarkersToMap()
    this.#adjustMapViewport()
  }

  async #createMapInstance() {
    const { Map } = await window.google.maps.importLibrary("maps")

    this.map = new Map(this.element, {
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      keyboardShortcuts: false,
      gestureHandling: "cooperative",
      mapId: this.mapIdValue,
    })
  }

  async #addMarkersToMap() {
    const { AdvancedMarkerElement } =
      await window.google.maps.importLibrary("marker")

    this.markers = []
    this.markersById = {}
    this.highlightedMarker = null
    this.bounds = new window.google.maps.LatLngBounds()

    this.markersValue.forEach((marker) => {
      this.#createMarker(marker, AdvancedMarkerElement)
    })
  }

  #createMarker(marker, AdvancedMarkerElement) {
    const position = { lat: marker.lat, lng: marker.lng }
    const markerElement = new AdvancedMarkerElement({
      position,
      map: this.map,
      title: marker.name,
    })
    const infoWindow = this.#createInfoWindow(marker.name)
    const handlers = this.#attachMarkerEvents(markerElement, infoWindow)

    if (marker.url) this.#navigateOnClick(markerElement, marker.url, handlers)

    const record = { markerElement, infoWindow, ...handlers }
    this.markers.push(record)
    if (marker.id) this.markersById[marker.id] = record

    this.bounds.extend(position)
  }

  #createInfoWindow(content) {
    return new window.google.maps.InfoWindow({
      content,
      headerDisabled: true,
    })
  }

  #attachMarkerEvents(marker, infoWindow) {
    const handlers = {
      mouseenter: () => infoWindow.open({ anchor: marker, map: this.map }),
      mouseleave: () => infoWindow.close(),
    }

    marker.element.addEventListener("mouseenter", handlers.mouseenter)
    marker.element.addEventListener("mouseleave", handlers.mouseleave)

    return handlers
  }

  #navigateOnClick(marker, url, handlers) {
    marker.element.style.cursor = "pointer"
    handlers.click = (event) => {
      event.stopPropagation()
      window.Turbo.visit(url)
    }
    marker.element.addEventListener("click", handlers.click)
  }

  #scaleMarker(markerElement, transform) {
    markerElement.content.style.transformOrigin = "bottom"
    markerElement.content.style.transform = transform
  }

  #adjustMapViewport() {
    this.map.fitBounds(this.bounds)

    if (this.markers.length === 1) {
      this.#setSingleMarkerZoom()
    }
  }

  #setSingleMarkerZoom() {
    window.google.maps.event.addListenerOnce(this.map, "bounds_changed", () =>
      this.map.setZoom(12)
    )
  }

  #cleanup() {
    this.#cleanupMarkers()
    this.#cleanupMap()
  }

  #cleanupMarkers() {
    if (!this.markers) return

    this.markers.forEach(({ markerElement, mouseenter, mouseleave, click }) => {
      markerElement.element.removeEventListener("mouseenter", mouseenter)
      markerElement.element.removeEventListener("mouseleave", mouseleave)
      if (click) markerElement.element.removeEventListener("click", click)

      markerElement.map = null
    })

    this.markers = null
    this.markersById = {}
  }

  #cleanupMap() {
    if (this.map) {
      this.map = null
    }
  }
}
